
export type OrderSumber = "komponen_darah" | "bank_darah";

export type OrderStatus =
  | "draft"
  | "dikirim"
  | "diterima"
  | "menunggu_sampel"
  | "dikerjakan"
  | "selesai"
  | "dibatalkan";

export type HasilStatus = "belum" | "draft" | "final";

export interface OpsiNilai {
  value: string;
  label: string;
}

export interface OrderBankDarahRow {
  sumber: OrderSumber;
  id: number;
  nomor: string | null;
  tanggal_permintaan: string | null;
  tanggal_order: string | null;
  register_id: string;
  pasien_rm: string;
  pasien_nama: string | null;
  dokter_peminta_nama: string | null;
  bangsal: string | null;
  jenis_order: "langsung" | "booking";
  jenis: string;
  jenis_label: string;
  unit_pemohon: { kode: string | null; nama: string | null };
  ringkasan: string | null;
  jumlah_kolf: number | null;
  status: OrderStatus;
  status_label: string;
  dikirim_at: string | null;
  diterima_at: string | null;
  dikerjakan_at: string | null;
  selesai_at: string | null;
  dibatalkan_at: string | null;
  alasan_batal: string | null;
  waktu_tunggu_terima_menit: number | null;
  waktu_proses_menit: number | null;
  serah_terima: {
    id: number;
    diterima_at: string | null;
    sampel_ada: boolean;
    golongan_darah: string | null;
    rhesus: string | null;
    is_locked: boolean;
  } | null;
  hasil_status: HasilStatus;
  hasil_id: number | null;
  hasil_petugas_user_id: number | null;
  hasil_inkompatibel: boolean;
  persetujuan: { transfusi: boolean; inkompatibel: boolean; plebotomi: boolean };
  persetujuan_wajib: string[];
  persetujuan_kurang: string[];
  tertransfusi: boolean;
  bisa_batal: boolean;
  alasan_tidak_bisa_batal: string | null;
}

export interface OrderBankDarahFilter {
  tgl_start: string | null;
  tgl_end: string | null;
  tgl_kolom?: "tanggal_permintaan" | "selesai_at" | "dibatalkan_at";
  status?: string[];
  jenis?: string[];
  unit_id?: string | null;
  term?: string | null;
}

export interface OrderBankDarahListResponse {
  rows: OrderBankDarahRow[];
  jenis_options: OpsiNilai[];
  status_options: OpsiNilai[];
}

export interface SerahTerimaSampelDarah {
  id?: number;
  diterima_at: string | null;
  golongan_darah: string | null;
  rhesus: string | null;
  sampel_ada: boolean;
  komponen_diminta: string | null;
  jumlah_permintaan: string | null;
  jumlah_tersedia: string | null;
  jaminan: string | null;
  keterangan: string | null;
  petugas_penerima_user_id?: number | null;
  petugas_penerima_nama?: string | null;
  signed_penerima_at?: string | null;
  pengantar_nama: string | null;
  pengantar_jabatan: string | null;
  pengantar_jabatan_lain: string | null;
  label_pengantar_jabatan?: string | null;
  is_locked?: boolean;
}

export interface OrderRingkas {
  sumber: OrderSumber;
  id: number;
  nomor: string | null;
  jenis: string;
  jenis_label: string;
  jenis_order: "langsung" | "booking";
  status: OrderStatus;
  status_label: string;
  register_id: string;
  pasien_rm: string;
  pasien_nama: string | null;
  bangsal: string | null;
  unit_pemohon_nama: string | null;
  diterima_at: string | null;
  alasan_batal: string | null;
}

export interface SerahTerimaResponse {
  order: OrderRingkas;
  serah_terima: SerahTerimaSampelDarah | null;
  prefill: Partial<SerahTerimaSampelDarah> | null;
  alasan_tidak_bisa_hapus: string | null;
  jabatan_options: OpsiNilai[];
}

export interface SerahTerimaSimpanResponse {
  order: OrderRingkas;
  serah_terima: SerahTerimaSampelDarah;
  alasan_tidak_bisa_hapus: string | null;
}

export interface RekapOrderSummary {
  total: number;
  per_status: Record<string, number>;
  per_jenis: {
    label: string;
    total: number;
    per_status: Record<string, number>;
  }[];
  per_unit: { kode: string; nama: string; total: number; dibatalkan: number }[];
  rata_tunggu_terima_menit: number | null;
  rata_proses_menit: number | null;
  terpotong: boolean;
}

export interface RekapOrderResponse extends OrderBankDarahListResponse {
  summary: RekapOrderSummary;
  maks_baris: number;
}

export const STATUS_WARNA: Record<string, string> = {
  dikirim: "orange",
  diterima: "blue",
  menunggu_sampel: "amber darken-2",
  dikerjakan: "indigo",
  selesai: "green",
  dibatalkan: "grey"
};

export type DaftarPasienStatus = "diproses" | "selesai" | "dibatalkan";

export interface DaftarPasienOrder {
  sumber: OrderSumber;
  id: number;
  nomor: string | null;
  jenis: string;
  jenis_label: string;
  jenis_order: "langsung" | "booking";
  status: OrderStatus;
  status_label: string;
  alasan_batal: string | null;
  hasil_status: HasilStatus;
  serah_terima_final: boolean;
  persetujuan_kurang: string[];
  bisa_batal: boolean;
  alasan_tidak_bisa_batal: string | null;
}

export interface DaftarPasienBankDarahRow {
  register_id: string;
  pasien_rm: string;
  pasien: {
    rm: string;
    nama: string | null;
    sex: string | null;
    alamat: string | null;
    umur: string | null;
  };
  tgl_in: string | null;
  dokter_nama: string | null;
  jaminan: string | null;
  unit_pemohon: { kode: string | null; nama: string | null };
  bangsal: string | null;
  kelas: string | null;
  nomor_tt: string | null;
  status: DaftarPasienStatus;
  status_label: string;
  persetujuan: { transfusi: boolean; inkompatibel: boolean; plebotomi: boolean };
  tanggal_order_terakhir: string | null;
  diterima_at: string | null;
  sumber: DaftarPasienSumber[];
  transfer_id: number | null;
  orders: DaftarPasienOrder[];
}

export type DaftarPasienSumber = "order" | "transfer";

export interface DaftarPasienBankDarahFilter {
  tgl_start: string | null;
  tgl_end: string | null;
  unit_id?: string | null;
  term?: string | null;
  status?: DaftarPasienStatus | "" | null;
}

export type RekapSerahTerimaSampel = "ada" | "tidak";

export interface RekapSerahTerimaFilter extends OrderBankDarahFilter {
  sampel?: RekapSerahTerimaSampel | "" | null;
}

export interface RekapSerahTerimaRow {
  key: string;
  id: number;
  sumber: OrderSumber;
  permintaan_id: number;
  diterima_at: string | null;
  golongan_darah: string | null;
  rhesus: string | null;
  sampel_ada: boolean;
  komponen_diminta: string | null;
  jumlah_permintaan: string | null;
  jumlah_tersedia: string | null;
  jaminan: string | null;
  keterangan: string | null;
  petugas_penerima_nama: string | null;
  signed_penerima_at: string | null;
  pengantar_nama: string | null;
  pengantar_jabatan: string | null;
  label_pengantar_jabatan: string | null;
  is_final: boolean;
  register_id: string;
  pasien_rm: string;
  pasien_nama: string | null;
  unit_pemohon: { kode: string | null; nama: string | null };
  bangsal: string | null;
  nomor: string | null;
  jenis: string;
  jenis_label: string;
  jenis_order: "langsung" | "booking";
  status: OrderStatus;
  status_label: string;
  dikirim_at: string | null;
  alasan_batal: string | null;
  jeda_kirim_terima_menit: number | null;
}

export interface RekapSerahTerimaSummary {
  total: number;
  diterima: number;
  draft: number;
  dibatalkan: number;
  per_unit: {
    kode: string;
    nama: string;
    total: number;
    draft: number;
  }[];
  rata_jeda_kirim_terima_menit: number | null;
  terpotong: boolean;
}

export interface RekapSerahTerimaResponse {
  rows: RekapSerahTerimaRow[];
  summary: RekapSerahTerimaSummary;
  jenis_options: OpsiNilai[];
  status_options: OpsiNilai[];
  maks_baris: number;
}

export interface SerahTerimaListResponse {
  rows: RekapSerahTerimaRow[];
  jabatan_options: OpsiNilai[];
}
